// Grid view + Add/Edit/Delete actions for a single child form in a
// multi-form transaction — fully buffer-backed.
//
// This form's rows live in an in-memory ``store`` owned higher up
// (TxnScreen for a header's direct child; the parent row's editor
// draft for a grandchild). Nothing here touches the server to read or
// write rows: the user fills/edits the whole nested tree and it commits
// atomically on the header Save. In EDIT mode the store lazy-loads the
// existing rows on first open.
//
// Add/Edit opens a nested modal that reuses FormRenderer for the child
// sql_model. A row's own children (grandchildren) are held on the editor
// draft under ``__children`` and saved with the row — so their tabs are
// usable immediately, with no "save the parent first" gate at any depth.
//
// Transport: the screen-level runtime (item_change / lazy child loads /
// validate) comes from useTxnScreenRuntime(); notifications ride the txn
// runtime's notifier. The package carries no HTTP client or toast library.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Eye, Loader2, Pencil, Plus, Trash2, Undo2, X } from 'lucide-react'
import type { ColumnDef, ItemChangeFormChange, ItemChangeResult, RowCompleteEvent, SqlModel, TransactionModel } from '../types'
import {
  actionCaptureOf, actionScope, isScanSessionAction, legacyActionToDef, normalizeItemChange, partitionActionsByScope,
  rowIsTicked, rowSelectOf, visionOnLineList, withCapturedValue, type ActionDef,
} from '../types'
import { ScanSession, type ScanSessionCheck, type ScanSessionDetail } from '../ScanSession'
import { TxnRuntimeProvider, useTxnRuntime } from '../context'
import type { TxnNotifier } from '../context'
import { attemptWithWarningConfirm, extractBlockingWarnings } from '../warnings'
import { useWarningConfirm } from '../WarningsConfirmModal'
import {
  actionErrorMessages, firstValidationError, useActionError, useValidationErrorPopup,
} from '../ActionErrorModal'
import {
  attemptActionWithConfirm, isActionConfirmCancelled, useActionConfirm,
} from '../ActionConfirmModal'
import { DisplayCell, displayPlainText, hasDisplayFormat, temporalCellText } from '../displayFormat'
import { useKeyboardShortcuts, useShortcutLayer } from '../keyboard'
import { systemCombo } from '../actionShortcuts'
import type { EmbedDetailMap, ValidationErrorItem, RunActionResponse } from '../contracts'
import {
  buildParentPk, childFormsOf, columnDbName, columnFormKey, ddlbDisplayLabel,
  isHidden, isKey, listOrderedColumns, literalDefaults, parseApiError,
  richCellText, listHeadingOf,
} from '../utils'
import {
  colKeyOf, orderedDisplayCols, widthOf, useColumnDnd, useResolvedGridLayout,
  RESIZE_HANDLE_STYLE, type GridLayoutApi,
} from '../list/columnLayout'
import { LayoutActions } from '../list/LayoutActions'
import { useTxnCustomize } from '../../customize/txnContext'
import { colPath as czColPath, formPath as czFormPath, setLeaf as czSetLeaf } from '../../customize/txnOps'
import { CustomizedDot, GearButton, HiddenTray, VIOLET } from '../../customize/ui'
import { isListHidden as czIsListHidden } from '../utils'
import FormRenderer, { focusFieldAnchor, RunActionBar } from '../FormRenderer'
import FormTabs from '../list/FormTabs'
import { executeValidate } from './contract'
import {
  applyFormsToChildren, childrenForEvent, formChangeTouchesRows, isReplacedRow,
  listAllChildRows, rowCompleteWanted, rowForEvent,
} from './detailsBuffer'
import { isInvokableRule } from '../list/useItemChange'
import { useTxnScreenRuntime } from './screenContext'
import { Z_CHILD_EDITOR } from '../../layers'
import LineEntryBar from '../lineEntry/LineEntryBar'
import HandheldCount from '../lineEntry/HandheldCount'
import { lineEntryOf } from '../lineEntry/model'
import { quickAddBinding, useQuickAdd } from '../lineEntry/QuickAdd'
import { tw } from '../../testHooks'
import {
  buildVisionActionLines, nextVisionLineNo as nextLineNo, visionLineNoColumn, type VisionLineCtx,
} from './visionLineActions'
import { useUdaScreen } from '../../uda/context'
import { useUdaRecord, useUdaRows } from '../../uda/useUdaResolve'
import {
  attributeGroups, detailsCounts, driverValues, groupCaptions, masterSourceNote, promotedColumns,
  withUdaColumns,
} from '../../uda/merge'
import UdaRowDrawer, { UdaDetailsChip } from '../../uda/UdaRowDrawer'
import { UdaValueCell } from '../../uda/cells'
import { evaluateFlags } from '../../uda/flagEval'
import { FlagIcons } from '../../uda/flags'
import { seedUdaProtect, udaProtectOf } from '../../uda/protect'
import { UdaNotice } from '../../uda/notice'
import VisionTaxPanel, { isVisionTaxForm } from '../list/VisionTaxPanel'

// In-memory row store for ONE child form, supplied by the parent. The rows
// are the buffer for this form; add/edit/remove mutate it (immutably, by
// array index); ensureLoaded lazily fills it from the server in edit mode.
export interface ChildStore {
  rows: Array<Record<string, unknown>>
  add: (row: Record<string, unknown>) => void
  edit: (index: number, patch: Record<string, unknown>) => void
  remove: (index: number) => void
  ensureLoaded: (parentPk: Record<string, unknown>) => void | Promise<void>
}

// Control keys that are not column values — stripped when an editor draft is
// seeded from a buffer row. ``__children`` is intentionally KEPT so a row's
// grandchildren travel with the draft and are re-saved with it.
const DRAFT_STRIP = new Set([
  '_txn_id', 'primary_key', '_localId', '_deleted', '_replaced', '_edited', '_saved',
  '_error', '_fieldErrors', 'apply_item_change', '_uda',
])

// A line's key for the additional-attributes resolve (echoed as `_key`).
function udaLineKey(r: Record<string, unknown>, i: number): string {
  return r._txn_id != null ? `t:${r._txn_id}` : r._localId != null ? `l:${r._localId}` : `i:${i}`
}

function rowToDraft(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row || {})) {
    if (!DRAFT_STRIP.has(k)) out[k] = v
  }
  return out
}

// Stable client-side id for a never-saved row (React key + reconciliation).
let _localIdSeq = 0
export function withLocalId(row: Record<string, unknown>): Record<string, unknown> {
  if (row._localId) return row
  _localIdSeq += 1
  return { ...row, _localId: `lp${_localIdSeq}` }
}

interface Props {
  project: string
  objName: string
  formNo: string
  sm: SqlModel
  parentSm: SqlModel
  // This form's parent key (the header's PK for a direct child, the edited
  // row's PK for a grandchild). Drives the store's lazy-load in edit mode.
  parentPk: Record<string, unknown>
  parentPkKey: string
  // Full header (parent form) values, used so a child-form column's
  // lookup filter can resolve {header.X} placeholders. Optional —
  // when omitted, only {form.X} references resolve.
  parentHeaderValues?: Record<string, unknown>
  // Live column layout (drag to resize / reorder). When the host supplies it,
  // the change can be applied back to the design (author only); otherwise it's
  // a local, per-session view tweak.
  gridLayout?: GridLayoutApi
  // The full transaction model, so this form can find its OWN child forms
  // (grandchildren) and render them nested inside a row's editor.
  model: TransactionModel
  // Nesting depth (header's direct child = 1, its child = 2, …). Drives the
  // editor modal z-index so stacked editors don't collide.
  depth?: number
  // In-memory rows + mutators for THIS form (see ChildStore).
  store: ChildStore
  // View-only: no Add / Edit / Delete controls (the grid just displays the
  // rows). Used by the approvals inbox when reviewing a staged record before
  // the approver explicitly chooses to edit it.
  readOnly?: boolean
  // A `row_select` form's tick boxes are disabled too (the whole record is
  // view-only — readOnly alone is always on for such a form).
  selectLocked?: boolean
  // A `lines_locked` form on an editable record: its lines open view-only
  // (readOnly), but their custom action buttons still show, and an action's
  // item_change result is written straight onto that line — business logic
  // filling a locked line is what the flag is for. Built-in add / edit /
  // delete stay off.
  lockedLineActions?: boolean
  // Presentation. 'grid' is the desktop tabular child grid. 'compact' is the
  // mobile-style inline card section (title + row mini-cards) whose row editor
  // opens full-screen with a single-column form. Recurses to grandchildren.
  variant?: 'grid' | 'compact'
  // Vision-style warnings confirm (OK / Cancel) — see TxnScreen.visionDialogs.
  visionDialogs?: boolean
  // The saved record these lines belong to (absent while it is new). Line
  // Entry sends it so the engine gates the pull as an edit of that record.
  recordTxnId?: string
  // Values a row event returned for an ANCESTOR form (the header, or this
  // form's parent row at deeper nesting), keyed by form_no — e.g. a
  // row_complete that also recomputes the header's totals. The parent applies
  // its own form's `set` and passes the rest further up. Absent ⇒ dropped.
  onAncestorPatch?: (forms: Record<string, ItemChangeFormChange>) => void
  // The rows are given (a Vision line's calculated taxes): no Add / Delete,
  // a row is only opened and edited.
  fixedRows?: boolean
}

// Self-providing wrapper. ChildFormTab is mounted as a SIBLING of the header
// FormRenderer (TxnScreen) and recursively inside its own row editor — and
// the worker below calls useTxnRuntime() (merged system actions / runAction).
// Like the original designer component it (re-)provides the txn runtime here,
// sourced from the screen runtime. Nested mounts re-wrap harmlessly (same
// value).
export default function ChildFormTab(props: Props) {
  const screen = useTxnScreenRuntime()
  return (
    <TxnRuntimeProvider value={screen.txn}>
      <ChildFormTabInner {...props} />
    </TxnRuntimeProvider>
  )
}

function ChildFormTabInner({
  project, objName, formNo, sm, parentSm, parentPk, parentPkKey,
  parentHeaderValues, gridLayout, model, depth = 1, store, readOnly = false,
  variant = 'grid', visionDialogs = false, recordTxnId, onAncestorPatch, fixedRows = false,
  selectLocked = false, lockedLineActions = false,
}: Props) {
  void parentSm
  const compact = variant === 'compact'
  const screen = useTxnScreenRuntime()
  const runtime = useTxnRuntime()
  const notify = runtime.notify
  const [loading, setLoading] = useState(false)
  // True while an on-demand form validation round-trip is in flight (disables
  // the row editor's buttons).
  const [busy, setBusy] = useState(false)

  const [editorOpen, setEditorOpen] = useState(false)
  // Index of the row being edited within store.rows (null while adding).
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [editorValues, setEditorValues] = useState<Record<string, unknown>>({})
  const [editorErrors, setEditorErrors] = useState<Record<string, string>>({})
  const [editorBanner, setEditorBanner] = useState<string | null>(null)
  // Whether the user changed a column in this editor — gates the _edited flag
  // so an unchanged existing row isn't needlessly rewritten on save.
  const [editorDirty, setEditorDirty] = useState(false)
  const editorValuesRef = useRef<Record<string, unknown>>({})
  useEffect(() => { editorValuesRef.current = editorValues }, [editorValues])
  // Warning codes the user OK'd in the last validation of the open row.
  const ackedWarningsRef = useRef<string[]>([])

  // This form's OWN child forms (grandchildren of the header). When non-empty,
  // the row editor gains nested child tabs (usable immediately — no gate).
  const myChildren = useMemo(() => childFormsOf(model, formNo), [model, formNo])
  // Which tab is active inside the row editor — this form's own form_no, or
  // one of its child form_nos.
  const [activeTab, setActiveTab] = useState<string>(formNo)

  const editingRow = editIndex !== null ? store.rows[editIndex] : null
  const isExistingRow = !!editingRow?._txn_id
  // The saved row's own id — an already-persisted row is validated as an EDIT
  // so record-aware rules (must_be_unique) exclude it from their own search.
  const editorTxnId = isExistingRow ? String(editingRow?._txn_id ?? '') : undefined
  // A line pulled through Line Entry carries the columns its source locks.
  const lineLocks = (editingRow?._protect as Record<string, 0 | 1> | undefined) || undefined

  // The parent_pk a nested child grid scopes its lazy-load by = this (edited)
  // row's own key values. Built from the editor draft; only the key columns
  // matter, so the stringified key is stable across non-key keystrokes.
  const childParentPk = useMemo(
    () => buildParentPk(sm, editorValues), [sm, editorValues],
  )
  const childParentPkKey = useMemo(
    () => JSON.stringify(childParentPk), [childParentPk],
  )

  // Columns in designer x-order with any live width/order override applied
  // (drag to resize / reorder — see useResolvedGridLayout).
  // Additional attributes: each line's applicable set (rows scope) — the
  // promoted ones become columns of the rows table (n/a where a line does
  // not have them), the rest live behind the Details chip.
  const udaApi = useUdaScreen()
  const udaRows = useUdaRows({
    formNo, rows: store.rows, rowKey: udaLineKey, headerValues: parentHeaderValues,
    txnId: recordTxnId, active: !!udaApi,
  })
  const udaListSm = useMemo(
    () => withUdaColumns(sm, promotedColumns(udaRows.columns)), [sm, udaRows.columns])
  const baseCols = useMemo(() => listOrderedColumns(udaListSm), [udaListSm])
  const rl = useResolvedGridLayout(gridLayout, formNo)
  const cols = useMemo(() => orderedDisplayCols(baseCols, rl.layout), [baseCols, rl.layout])
  // Edit + Delete (the "Actions" heading sets the floor); additional
  // attributes add their flags and Details chip to the cell.
  const actionsW = udaRows.resolved ? 110 : 84
  // Columns kept in view (the organization's `frozen_columns`) — sticky left
  // cells; Customize mode adds ⚙ per header, a freeze stepper and a tray.
  const cz = useTxnCustomize()
  const frozen = Math.max(0, Math.min(Number(sm.frozen_columns) || 0, cols.length))
  const stickyLeft = (i: number): React.CSSProperties | undefined => {
    if (i >= frozen) return undefined
    let left = 0
    for (let k = 0; k < i; k++) left += wOf(cols[k])
    return {
      position: 'sticky', left, zIndex: 1,
      background: 'var(--lp-input-bg, var(--color-bg))',
      boxShadow: i === frozen - 1 ? `2px 0 0 ${cz ? VIOLET : 'var(--color-border)'}` : undefined,
    }
  }
  const setFrozen = (n: number) => {
    if (!cz) return
    const next = Math.max(0, Math.min(n, cols.length))
    void cz.session.apply(czSetLeaf(czFormPath(formNo, 'frozen_columns'), next),
      next ? `Freeze ${next} column${next === 1 ? '' : 's'}` : 'Unfreeze')
  }
  // A width the user dragged wins; otherwise never narrower than the
  // heading's longest word (imported Vision widths cut "SHELF" to "SHEL").
  const wOf = useCallback((c: ColumnDef) => {
    const w = widthOf(c, rl.layout)
    if (rl.layout?.widths[colKeyOf(c)] != null) return w
    const word = Math.max(0, ...String(listHeadingOf(c) || '').split(/\s+/).map(s => s.length))
    return Math.max(w, word * 9 + 26)
  }, [rl.layout])
  const dnd = useColumnDnd({
    displayKeys: useMemo(() => cols.map(colKeyOf), [cols]),
    enabled: true,
    onReorder: rl.setOrder,
    onResize: rl.setWidth,
  })

  // ── Additional attributes (user-defined attributes) ───────────────────
  // The rows table shows each line's Details chip (rows scope); the row
  // editor asks for ITS line (record scope, re-asked when a deciding field
  // changes) and merges the applicable attributes into the form — so they
  // edit, item-change and validate like the line's own columns. Inert
  // without the screen's store.
  const udaEditor = useUdaRecord({
    formNo, values: editorValues, headerValues: parentHeaderValues, txnId: recordTxnId,
    active: !!udaApi && editorOpen,
  })
  const udaEditorSm = useMemo(() => withUdaColumns(sm, udaEditor.columns), [sm, udaEditor.columns])
  const udaEditorSmRef = useRef(udaEditorSm)
  udaEditorSmRef.current = udaEditorSm
  // A locked line's editor offers only the custom buttons (see
  // lockedLineActions); the built-in ones would only be refused.
  const lineActionsOn = lockedLineActions && readOnly
  const lockedEditorSm = useMemo(() => (lineActionsOn
    ? { ...udaEditorSm, actions: (udaEditorSm.actions || []).filter(a => legacyActionToDef(a).handler !== 'system') }
    : udaEditorSm), [lineActionsOn, udaEditorSm])
  const udaOwnGroups = useMemo(
    () => new Set((sm.columns || []).map(w => String(w?.column?.group_name || 'Others'))), [sm])
  const udaEditorCaptions = useMemo(() => {
    if (!udaEditor.sets.length) return undefined
    // `form.<col>` is this line's column, `header.<col>` the parent form's.
    const columnOf = (via: string) => {
      const [side, ...rest] = String(via || '').split('.')
      const name = rest.join('.').trim().replace(/\s+/g, '_').toLowerCase()
      const host = side === 'header' ? parentSm : sm
      return (host?.columns || []).map(w => w.column).find(c => !!c && columnFormKey(c) === name)
    }
    const caps = groupCaptions(udaEditor.sets, udaOwnGroups,
      (why) => masterSourceNote(why, udaEditor.columns, columnOf))
    return Object.keys(caps).length ? caps : undefined
  }, [udaEditor.sets, udaEditor.columns, sm, parentSm, udaOwnGroups])
  const udaEditorHeading = useMemo(() => {
    const groups = attributeGroups(udaEditor.columns, udaOwnGroups)
    return groups.length ? { groups, label: 'Additional details' } : undefined
  }, [udaEditor.columns, udaOwnGroups])
  // The open line's attribute lock state: seeded from the attributes' own
  // protect rules once the answer brings them (dropped when another line has
  // opened since), then kept by their live item changes.
  const [udaEditorLocks, setUdaEditorLocks] = useState<Record<string, 0 | 1>>({})
  const udaEditorEpoch = useRef(0)
  useEffect(() => { udaEditorEpoch.current += 1; setUdaEditorLocks({}) }, [editorOpen, editIndex])
  const udaEditorSeeded = useRef('')
  useEffect(() => {
    if (!editorOpen || !udaEditor.resolved || udaEditor.columns.length === 0) return
    const epoch = udaEditorEpoch.current
    const mark = `${epoch}|${udaEditor.defVersion}|${udaEditor.columns.map(c => c.name).join(',')}`
    if (mark === udaEditorSeeded.current) return
    udaEditorSeeded.current = mark
    void seedUdaProtect(udaEditor.columns, editorValuesRef.current, async (col, hdr) => (await screen.execute(
      objName, 'item_change', {
        form_no: formNo, field_name: columnFormKey(col), header: hdr,
        header_values: parentHeaderValues || {},
      })) as ItemChangeResult)
      .then((locks) => {
        if (epoch !== udaEditorEpoch.current || !Object.keys(locks).length) return
        setUdaEditorLocks(prev => ({ ...prev, ...locks }))
      })
      .catch(() => { /* best-effort: the line stays editable */ })
    // parentHeaderValues is read as of the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, udaEditor.resolved, udaEditor.columns, udaEditor.defVersion, screen, objName, formNo])
  const editorLocks = useMemo(() => (Object.keys(udaEditorLocks).length
    ? { ...(lineLocks || {}), ...udaEditorLocks } : lineLocks), [lineLocks, udaEditorLocks])
  // sm.lines_locked_editable: on an editable record a locked line opens for
  // editing (Save shown) with only those columns open.
  const lockedEditableKeys = useMemo(() => {
    const names = (sm.lines_locked_editable || []).map(n => String(n).trim().toLowerCase()).filter(Boolean)
    if (!lineActionsOn || !names.length) return null
    return new Set((sm.columns || []).map(w => w.column)
      .filter(c => c && (names.includes(String(c.name || '').toLowerCase())
        || names.includes(String(c.db_name || '').toLowerCase())))
      .map(columnFormKey))
  }, [sm, lineActionsOn])
  const lineEditable = !!lockedEditableKeys?.size
  const editorViewOnly = readOnly && !lineEditable
  const lineEditorLocks = useMemo(() => {
    if (!lockedEditableKeys?.size) return editorLocks
    const locks: Record<string, 0 | 1> = { ...(editorLocks || {}) }
    for (const w of udaEditorSm.columns || []) {
      const k = w.column ? columnFormKey(w.column) : ''
      if (k && !lockedEditableKeys.has(k)) locks[k] = 1
    }
    return locks
  }, [lockedEditableKeys, editorLocks, udaEditorSm])
  // The rows table's Details drawer (the line it shows, by key) and each
  // line's attribute lock state, seeded when its drawer opens.
  const [udaDrawerKey, setUdaDrawerKey] = useState<string | null>(null)
  const [udaRowLocks, setUdaRowLocks] = useState<Record<string, Record<string, 0 | 1>>>({})
  const udaDrawerTimer = useRef<number | null>(null)
  useEffect(() => () => {
    if (udaDrawerTimer.current != null) window.clearTimeout(udaDrawerTimer.current)
  }, [])
  useEffect(() => {
    if (!udaDrawerKey || !udaRows.resolved) return
    const key = udaDrawerKey
    const i = store.rows.findIndex((r, j) => udaLineKey(r, j) === key)
    const res = udaRows.rowOf(key)
    if (i < 0 || !res) return
    let cancelled = false
    void seedUdaProtect(udaRows.columns, store.rows[i], async (col, hdr) => (await screen.execute(
      objName, 'item_change', {
        form_no: formNo, field_name: columnFormKey(col), header: hdr,
        header_values: parentHeaderValues || {},
      })) as ItemChangeResult, res.attr_keys)
      .then((locks) => {
        if (cancelled || !Object.keys(locks).length) return
        setUdaRowLocks(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...locks } }))
      })
      .catch(() => { /* best-effort: the line stays editable */ })
    return () => { cancelled = true }
    // Seeded once per opened line (and answer); the rows themselves move on
    // every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [udaDrawerKey, udaRows.resolved, udaRows.columns, udaRows.defVersion])

  // Debounced item_change for the line form. Fires 700ms after the user
  // pauses editing a column that declares a rule/code handler, threading the
  // parent/header form's values through so expression fills can resolve
  // {header.X} references. Best-effort: failures are logged, never thrown.
  const itemChangeTimer = useRef<number | null>(null)
  useEffect(() => () => {
    if (itemChangeTimer.current != null) window.clearTimeout(itemChangeTimer.current)
  }, [])

  const triggerItemChange = useCallback((
    fieldName: string, header: Record<string, unknown>,
  ) => {
    // The editor's model, additional attributes included (their own item
    // change fires like any column's).
    const col = (udaEditorSmRef.current.columns || []).find(w => {
      const c = w.column
      if (!c) return false
      return c.name === fieldName || columnFormKey(c) === fieldName
    })?.column
    if (!col) return
    const ic = normalizeItemChange(col.item_change)
    if (!ic) return
    if (ic.mode === 'code' && !ic.code.trim()) return
    // A rule is invokable as long as it has fills — a pure-expression rule
    // has no source_table.
    if (ic.mode === 'rule' && (!ic.fill || ic.fill.length === 0)) return

    if (itemChangeTimer.current != null) window.clearTimeout(itemChangeTimer.current)
    itemChangeTimer.current = window.setTimeout(async () => {
      try {
        const res = await screen.execute(objName, 'item_change', {
          field_name: fieldName,
          form_no: formNo,
          header,
          header_values: parentHeaderValues || {},
        }) as {
          ok: boolean; updates?: Record<string, unknown>; error?: string;
          warning?: string; warnings?: Array<{ message: string; code?: string | null }>
          prompts?: Array<{ message: string; code?: string | null }>
        }
        if (res?.error) {
          notify.error(`item_change failed: ${res.error}`)
          return
        }
        const warn = notify.warn ?? notify.info ?? notify.success
        const info = notify.info ?? notify.success
        if (res?.warnings?.length) res.warnings.forEach(w => warn(w.message))
        else if (res?.warning) warn(res.warning)
        res?.prompts?.forEach(p => info(p.message))
        const locks = udaProtectOf((res as { protect?: Record<string, unknown> })?.protect)
        if (Object.keys(locks).length) setUdaEditorLocks(prev => ({ ...prev, ...locks }))
        const updates = res?.updates || {}
        if (Object.keys(updates).length === 0) return
        setEditorValues(prev => ({ ...prev, ...updates }))
      } catch (err) {
        const { message } = parseApiError(err)
        if (typeof console !== 'undefined') {
          console.debug('item_change unavailable (best-effort):', message)
        }
      }
    }, 700)
  }, [screen, objName, sm, formNo, parentHeaderValues, notify])

  // The line form's ROW events: row_add when a new line opens, row_edit when an
  // existing one does — the form-level rule, with the header's values like a
  // column item_change. An imported Vision detail form declares its item-change
  // bean here (itm_default / itm_defaultedit): the call that copies the header's
  // values onto the line, SO Amendment's SALE_ORDER among them, without which
  // the line-number item change finds no order line and fills nothing.
  const fireRowEvent = useCallback(async (
    which: 'row_add' | 'row_edit', row: Record<string, unknown>,
  ) => {
    if (!isInvokableRule(normalizeItemChange(which === 'row_add' ? sm.row_add : sm.row_edit))) return
    try {
      const res = await screen.execute(objName, which, {
        form_no: formNo,
        header: row,
        header_values: parentHeaderValues || {},
      }) as { updates?: Record<string, unknown>; error?: string }
      if (res?.error) {
        notify.error(`${which} failed: ${res.error}`)
        return
      }
      const updates = res?.updates || {}
      if (Object.keys(updates).length === 0) return
      setEditorValues(prev => ({ ...prev, ...updates }))
    } catch (err) {
      const { message } = parseApiError(err)
      if (typeof console !== 'undefined') {
        console.debug(`${which} unavailable (best-effort):`, message)
      }
    }
  }, [screen, objName, sm, formNo, parentHeaderValues, notify])

  // The row's values as of its last row_complete in THIS editor session — the
  // `_old` the next completion compares against, so finishing the row twice
  // (child tab, then Save) does not rebuild over edits made in between.
  const lastCompletedRef = useRef<Record<string, unknown> | null>(null)

  // row_complete: the user FINISHED the row (it passed its row validation).
  // The form's business logic gets the row, its previous values, its current
  // child rows and its sibling lines, and returns what to do with each child
  // form (replace / clear / unchanged) plus values for the row and its
  // ancestors. Returns the completed draft — or null when the function
  // refused the row (its error is shown and the row is not kept).
  const fireRowComplete = useCallback(async (
    draft: Record<string, unknown>,
  ): Promise<{ draft: Record<string, unknown>; changed: boolean } | null> => {
    const ev = sm.row_complete
    const ic = normalizeItemChange(ev as RowCompleteEvent | string | undefined)
    if (readOnly || !isInvokableRule(ic)) return { draft, changed: false }
    const settings: Partial<RowCompleteEvent> = (typeof ev === 'object' && ev) ? ev : {}
    const old = lastCompletedRef.current ?? (editingRow ? rowToDraft(editingRow) : null)
    if (!rowCompleteWanted(settings.when_changed, draft, old)) return { draft, changed: false }
    let res: {
      ok?: boolean; updates?: Record<string, unknown>; error?: string; warning?: string
      warnings?: Array<{ message: string; code?: string | null }>
      prompts?: Array<{ message: string; code?: string | null }>
      forms?: Record<string, ItemChangeFormChange>
    }
    try {
      res = await screen.execute(objName, 'row_complete', {
        form_no: formNo,
        header: rowForEvent(draft),
        header_values: parentHeaderValues || {},
        old: old ? rowForEvent(old) : {},
        children: childrenForEvent(draft.__children as EmbedDetailMap | undefined),
        lines: store.rows.filter((r, i) => i !== editIndex && !r._deleted).map(rowForEvent),
        ...(recordTxnId ? { txn_id: recordTxnId } : {}),
      }) as typeof res
    } catch (err) {
      const { message } = parseApiError(err)
      setEditorBanner(message)
      notify.error(message)
      return null
    }
    if (res?.error || res?.ok === false) {
      setEditorBanner(res?.error || 'The row could not be completed')
      notify.error(res?.error || 'The row could not be completed')
      return null
    }
    const warn = notify.warn ?? notify.info ?? notify.success
    const info = notify.info ?? notify.success
    if (res?.warnings?.length) res.warnings.forEach(w => warn(w.message))
    else if (res?.warning) warn(res.warning)
    res?.prompts?.forEach(p => info(p.message))

    const forms = res?.forms || {}
    const ownSet = { ...(res?.updates || {}), ...(forms[formNo]?.set || {}) }
    const childNos = myChildren.map(c => c.formNo)
    const kids = applyFormsToChildren(
      draft.__children as EmbedDetailMap | undefined, forms, childNos, withLocalId)
    const next: Record<string, unknown> = { ...draft, ...ownSet }
    if (kids.changed) next.__children = kids.children
    // Everything addressed to a form that is neither this one nor one of its
    // children belongs to an ancestor (the header's totals…).
    const up: Record<string, ItemChangeFormChange> = {}
    for (const [fno, change] of Object.entries(forms)) {
      if (fno === formNo || childNos.includes(fno)) continue
      if (change?.set && Object.keys(change.set).length) up[fno] = change
    }
    if (Object.keys(up).length) onAncestorPatch?.(up)
    lastCompletedRef.current = rowToDraft(next)
    return { draft: next, changed: kids.changed || Object.keys(ownSet).length > 0 }
  }, [screen, objName, sm, formNo, parentHeaderValues, notify, readOnly, editingRow,
      store.rows, editIndex, recordTxnId, myChildren, onAncestorPatch])

  // A DESCENDANT's row event addressed this form (or one above it): apply our
  // own form's values to the open draft and pass the rest further up.
  const handleDescendantPatch = useCallback((forms: Record<string, ItemChangeFormChange>) => {
    const mine = forms[formNo]?.set
    if (mine && Object.keys(mine).length) {
      setEditorValues(prev => ({ ...prev, ...mine }))
      setEditorDirty(true)
    }
    const rest: Record<string, ItemChangeFormChange> = {}
    for (const [fno, change] of Object.entries(forms)) if (fno !== formNo) rest[fno] = change
    if (Object.keys(rest).length) onAncestorPatch?.(rest)
  }, [formNo, onAncestorPatch])

  // Lazy-load existing grandchildren of the row being edited into the draft's
  // __children (edit mode only; a brand-new row has none). Guarded so a slow
  // load never clobbers grandchild edits the user already started.
  const ensureGrandchildLoaded = useCallback(async (
    gcFormNo: string, gcParentPk: Record<string, unknown>,
  ) => {
    if (!gcParentPk || Object.keys(gcParentPk).length === 0) return
    const ch = (editorValuesRef.current.__children as EmbedDetailMap | undefined) || {}
    if (Object.prototype.hasOwnProperty.call(ch, gcFormNo)) return
    try {
      const rows = await listAllChildRows(
        (q) => screen.list(objName, q), gcFormNo, gcParentPk,
      )
      setEditorValues(prev => {
        const c = (prev.__children as EmbedDetailMap | undefined) || {}
        if (Object.prototype.hasOwnProperty.call(c, gcFormNo)) return prev
        return { ...prev, __children: { ...c, [gcFormNo]: rows } }
      })
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.debug('lazy-load grandchild rows failed (best-effort):', err)
      }
    }
  }, [screen, objName])

  // Lazy-load THIS form's own rows when the tab opens (edit mode). The store's
  // ensureLoaded is a no-op in add mode or when already loaded.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.resolve(store.ensureLoaded(parentPk))
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // parentPkKey is the stable serialised form of parentPk — re-run when its
    // content changes, not on every reference update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentPkKey, formNo])

  // A store backed by the editor draft's __children for one grandchild form.
  // Mutating it updates editorValues, so the grandchild rows travel with the
  // draft and are persisted when this row is saved.
  // Any grandchild change marks this row dirty, so a SAVED row whose only
  // change is in its children is still flagged _edited — the batch save skips
  // an untouched existing row, and its children's writes must not be lost.
  const makeChildStore = (gcFormNo: string): ChildStore => ({
    rows: ((editorValues.__children as EmbedDetailMap | undefined)?.[gcFormNo]) || [],
    add: (row) => { setEditorDirty(true); setEditorValues(prev => {
      const ch = (prev.__children as EmbedDetailMap | undefined) || {}
      return {
        ...prev,
        __children: { ...ch, [gcFormNo]: [...(ch[gcFormNo] || []), withLocalId(row)] },
      }
    }) },
    edit: (index, patch) => { setEditorDirty(true); setEditorValues(prev => {
      const ch = (prev.__children as EmbedDetailMap | undefined) || {}
      const rows = (ch[gcFormNo] || []).slice()
      const target = rows[index]
      if (!target) return prev
      // Drop any stale row-level error when the row is touched.
      const { _error, _fieldErrors, ...rest } = target as Record<string, unknown>
      void _error; void _fieldErrors
      rows[index] = { ...rest, ...patch }
      return { ...prev, __children: { ...ch, [gcFormNo]: rows } }
    }) },
    remove: (index) => { setEditorDirty(true); setEditorValues(prev => {
      const ch = (prev.__children as EmbedDetailMap | undefined) || {}
      const rows = ch[gcFormNo] || []
      const target = rows[index]
      if (!target) return prev
      const nextRows = target._txn_id
        ? rows.map((r, i) => (i === index ? { ...r, _deleted: true } : r))
        : rows.filter((_, i) => i !== index)
      return { ...prev, __children: { ...ch, [gcFormNo]: nextRows } }
    }) },
    // Only an existing (saved) child row has server-side grandchildren to
    // load; a brand-new row's grandchildren live only in this draft. Gating on
    // the render-time isExistingRow avoids a stray fetch (and the key-collision
    // risk of loading another record's rows) for a new row with a typed key.
    ensureLoaded: (gcParentPk) => {
      if (!isExistingRow) return
      return ensureGrandchildLoaded(gcFormNo, gcParentPk)
    },
  })

  // An imported Vision line form numbers each line it adds the way Vision's
  // MasterStatefulEJB.assignNewLineNo does: LINE_NO = the highest line + 1,
  // blank-padded to a CHAR column's width ("  1"). Only a LINE_NO key that is
  // not the parent's.
  const visionLineCol = useMemo(
    () => visionLineNoColumn(sm, parentPk, !!visionDialogs), [visionDialogs, sm, parentPk])
  const nextVisionLineNo = (after = 0): string => nextLineNo(visionLineCol, store.rows, after)

  const openAdd = () => {
    const draft = literalDefaults(sm)
    if (visionLineCol) draft[columnFormKey(visionLineCol)] = nextVisionLineNo()
    setEditIndex(null)
    setEditorValues(draft)
    setEditorErrors({})
    setEditorBanner(null)
    setEditorDirty(false)
    setActiveTab(formNo)
    setEditorOpen(true)
    lastCompletedRef.current = null
    void fireRowEvent('row_add', draft)
  }

  // ``view`` opens the row read-only (the header is locked — e.g. a confirmed
  // transaction): same editor, no Save, and no row_edit event, since nothing
  // may change and a view must not run the line's business logic.
  const openEdit = (index: number, view = false) => {
    const row = store.rows[index]
    if (!row) return
    setCurrentLineKey(lineKeyOf(row))
    const draft = rowToDraft(row)
    setEditIndex(index)
    setEditorValues(draft)
    setEditorErrors((row._fieldErrors as Record<string, string>) || {})
    setEditorBanner((row._error as string) || null)
    setEditorDirty(false)
    setActiveTab(formNo)
    setEditorOpen(true)
    lastCompletedRef.current = null
    if (!view) void fireRowEvent('row_edit', draft)
  }

  const closeEditor = () => {
    setEditorOpen(false)
    setEditIndex(null)
    setEditorValues({})
    setEditorErrors({})
    setEditorBanner(null)
    setEditorDirty(false)
    setActiveTab(formNo)
  }

  // ── Merged system actions (add / edit) ──────────────────────────────────
  // A system 'add' / 'edit' action defined on THIS child form doesn't render
  // as a separate button — it merges into the built-in "Add row" button /
  // row pencil (label / hover text), and clicking the built-in routes through
  // run_action so the action's pre-action validations gate the operation.
  // Same contract as the header list (see partitionActionsByScope).
  const { confirmWarnings, warningsModal } = useWarningConfirm(visionDialogs ? 'OK' : undefined)
  // Every action button's failure: an Error popup with only OK.
  const { showActionError, actionErrorModal } = useActionError()
  // Imported Vision apps: the line's validation errors pop up one at a time
  // (code + description, OK only); OK puts the cursor on the failing column.
  const { showValidationError, validationErrorModal } = useValidationErrorPopup()
  const { confirmAction, actionConfirmModal } = useActionConfirm()
  const { systemOverrides } = useMemo(
    () => partitionActionsByScope(sm.actions), [sm])

  const runMergedSystemAction = async (
    name: string,
    target: { mode: 'add' } | { mode: 'edit'; index: number },
  ) => {
    setBusy(true)
    try {
      const row = target.mode === 'edit' ? store.rows[target.index] : undefined
      const res = await attemptActionWithConfirm(
        (confirm) => attemptWithWarningConfirm(
          (ignoreWarnings) => runtime.runAction(project, objName, {
            formNo,
            actionName: name,
            header: row ? rowToDraft(row) : {},
            headerValues: parentHeaderValues,
            parentPk,
            ...(row?._txn_id ? { txnId: String(row._txn_id) } : {}),
            ...(ignoreWarnings.length ? { ignoreWarnings } : {}),
            ...(confirm ? { confirm } : {}),
          }),
          confirmWarnings,
        ),
        confirmAction,
      )
      const info = notify.info ?? notify.success
      res.prompts?.forEach(p => info(p.message))
      if (res.handler === 'system') {
        if (res.system_op === 'add') openAdd()
        else if (res.system_op === 'edit' && target.mode === 'edit') openEdit(target.index)
      }
    } catch (err) {
      if (isActionConfirmCancelled(err)) return  // user backed out of the gate
      // No editor is open yet — the action stopped: Error popup, OK only.
      showActionError(actionErrorMessages(parseApiError(err)))
    } finally {
      setBusy(false)
    }
  }

  // Every way of adding / opening a row goes through the override (when one
  // is defined) so its validation gate can't be bypassed.
  const addOverride = systemOverrides.add
  const editOverride = systemOverrides.edit
  const gridAdd = () => {
    if (addOverride) void runMergedSystemAction(addOverride.name, { mode: 'add' })
    else openAdd()
  }
  const gridEdit = (index: number) => {
    if (editOverride) void runMergedSystemAction(editOverride.name, { mode: 'edit', index })
    else openEdit(index)
  }
  // Opening a line to LOOK at it is not an edit, so it skips the Edit
  // action's gate.
  const gridView = (index: number) => openEdit(index, true)

  // The line-grid buttons' CURRENT line (Vision's current detail record): the
  // line last clicked, opened or deleted. Kept by key, not position, and kept
  // after a delete — Vision's Stock on adj_iss reads the item of the line just
  // deleted (sent with updateFlag D) when no other line is left.
  const [currentLineKey, setCurrentLineKey] = useState<string | null>(null)
  const lineKeyOf = (r: Record<string, unknown> | undefined) =>
    !r ? null : r._txn_id != null ? `t:${r._txn_id}` : r._localId != null ? `l:${r._localId}` : null

  // `row_select`: a tick box per row in place of Edit / Delete; the save sends
  // ticked rows only (TxnScreen.detailsForSave).
  const rowSelect = rowSelectOf(sm)
  const setTicked = (index: number, on: boolean) => {
    const row = store.rows[index]
    if (!rowSelect || !row || selectLocked) return
    store.edit(index, { [rowSelect.column]: on ? 'Y' : 'N', ...(row._txn_id ? { _edited: true } : {}) })
  }
  const tickBox = (row: Record<string, unknown>, index: number) => rowSelect ? (
    <input
      type="checkbox"
      aria-label={`Select line ${index + 1}`}
      {...tw('action', 'select_line')}
      checked={rowIsTicked(row, rowSelect)}
      disabled={selectLocked || busy}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setTicked(index, e.target.checked)}
      style={{ width: 16, height: 16, cursor: selectLocked ? 'default' : 'pointer', margin: 0 }}
    />
  ) : null
  // A saved line its criteria no longer offer is flagged _deleted (removed by
  // the save) and simply not shown.
  const hiddenRow = (row: Record<string, unknown>) => !!rowSelect && !!row._deleted
  const shownRows = store.rows.filter(r => !hiddenRow(r))
  const allTicked = !!rowSelect && shownRows.length > 0
    && shownRows.every(r => rowIsTicked(r, rowSelect))
  const tickAll = (on: boolean) => {
    if (!rowSelect || selectLocked) return
    store.rows.forEach((r, i) => {
      if (!hiddenRow(r) && rowIsTicked(r, rowSelect) !== on) setTicked(i, on)
    })
  }

  const toggleDelete = (index: number) => {
    const row = store.rows[index]
    if (!row) return
    setCurrentLineKey(lineKeyOf(row))
    if (row._deleted) { store.edit(index, { _deleted: false }); return }
    if (!window.confirm('Delete this row?')) return
    store.remove(index)
  }

  const onEditorFieldChange = (name: string, v: unknown) => {
    const next = { ...editorValues, [name]: v }
    setEditorValues(next)
    setEditorDirty(true)
    triggerItemChange(name, next)
  }

  // Validate this row's FORM-LEVEL rules on demand (no write). Form-exit
  // validation: fires when the row is saved or the user switches to one of its
  // child tabs. Shows the messages in the editor and returns false to block.
  const validateRowForm = async (): Promise<boolean> => {
    ackedWarningsRef.current = []
    // Nothing to validate on exit → skip the round-trip. A row shown in view
    // mode cannot change, so it is never validated either.
    if (readOnly || !formNeedsValidation(udaEditorSmRef.current)) return true
    setBusy(true)
    setEditorErrors({})
    setEditorBanner(null)
    const payloadHeader: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(editorValues)) {
      if (k !== '__children' && k !== '_ack_warnings') payloadHeader[k] = v
    }
    try {
      // Warnings (type W) get the OK / Cancel confirm, as in Vision: OK
      // re-validates with them acknowledged and accepts the row; Cancel keeps
      // the editor open so the value can be changed. Errors still block.
      await attemptWithWarningConfirm(
        (ignore) => {
          ackedWarningsRef.current = ignore
          return executeValidate(screen, objName, formNo, payloadHeader, {
            headerValues: parentHeaderValues,
            parentPk,
            // The row's own id when it is already saved; undefined for a row
            // the user just added to the buffer.
            txnId: editorTxnId,
            ignoreWarnings: ignore,
          })
        },
        confirmWarnings,
      )
      return true
    } catch (err) {
      ackedWarningsRef.current = []
      const { message, validationErrors } = parseApiError(err)
      if (extractBlockingWarnings(err)) {
        // The user chose Cancel on the warnings: point at the fields, but it
        // is not an error, so no error toast.
        const fieldMap: Record<string, string> = {}
        for (const it of validationErrors) {
          if (it.field && it.field !== '_record') fieldMap[it.field] = it.message
        }
        setEditorErrors(fieldMap)
        return false
      }
      const first = visionDialogs ? firstValidationError(validationErrors) : null
      if (first) {
        const field = first.field && first.field !== '_record' ? first.field : null
        setEditorErrors(field ? { [field]: first.message } : {})
        setEditorBanner(null)
        showValidationError({
          message: first.message,
          code: first.code,
          fieldLabel: field ? columnLabelFor(sm, field) : null,
          onOk: field ? () => focusFieldAnchor(field) : undefined,
        })
        return false
      }
      applyValidationErrors(validationErrors, message, setEditorErrors, setEditorBanner, notify)
      return false
    } finally {
      setBusy(false)
    }
  }

  // Write the editor draft (its columns + any grandchildren under __children)
  // back to the buffer. No server round-trip — the whole tree commits on the
  // header Save. For a touched existing row we flag _edited so the engine
  // rewrites it. Returns the row's index for keeping the editor open.
  const persistDraft = (values: Record<string, unknown> = editorValues, touched = false): number => {
    // The warnings OK'd for this row ride on it so the header Save does not
    // ask for them again (acknowledgedWarnings in detailsBuffer).
    const acked = ackedWarningsRef.current
    const ackPatch = { _ack_warnings: acked.length ? acked : undefined }
    if (editIndex === null) {
      const newIndex = store.rows.length
      store.add({ ...values, ...ackPatch })
      return newIndex
    }
    const patch: Record<string, unknown> = { ...values, ...ackPatch }
    if (isExistingRow && (editorDirty || touched)) patch._edited = true
    store.edit(editIndex, patch)
    return editIndex
  }

  // Run row_complete on the current draft and adopt its result in the editor.
  const completeRow = async () => {
    if (readOnly || !isInvokableRule(normalizeItemChange(sm.row_complete as RowCompleteEvent | string | undefined))) {
      return { draft: editorValuesRef.current, changed: false }
    }
    setBusy(true)
    try {
      // A SAVED row's child rows must be in the draft before the function
      // runs: it decides from them, and a replace has to delete them — an
      // unloaded child form would otherwise keep its old rows and collide with
      // the rebuilt ones at save.
      let draft = editorValuesRef.current
      if (isExistingRow && myChildren.length) {
        const ch = { ...((draft.__children as EmbedDetailMap | undefined) || {}) }
        let loaded = false
        const pk = buildParentPk(sm, draft)
        if (Object.keys(pk).length) {
          for (const c of myChildren) {
            if (Object.prototype.hasOwnProperty.call(ch, c.formNo)) continue
            try {
              ch[c.formNo] = await listAllChildRows(
                (q) => screen.list(objName, q), c.formNo, pk)
              loaded = true
            } catch (err) {
              const { message } = parseApiError(err)
              setEditorBanner(`Could not read the existing ${c.sm.form_title || 'child'} rows: ${message}`)
              return null
            }
          }
        }
        if (loaded) draft = { ...draft, __children: ch }
      }
      const done = await fireRowComplete(draft)
      if (!done) return null
      if (done.changed || done.draft !== editorValuesRef.current) {
        setEditorValues(done.draft)
        editorValuesRef.current = done.draft
        setEditorDirty(true)
      }
      return done
    } finally {
      setBusy(false)
    }
  }

  // Save the row into the buffer. For a LEAF form (no child forms) this closes
  // the editor. For a form that HAS children we keep the editor open (unless
  // asked to close) so its nested child tabs stay usable; we point the editor
  // at the just-written row so further grandchild edits land on it.
  const saveRow = async (closeAfter = false) => {
    // Form-exit validation runs before the row is committed to the buffer,
    // then the row's row_complete event (rebuilds its child forms).
    if (!(await validateRowForm())) return
    const done = await completeRow()
    if (!done) return
    const idx = persistDraft(done.draft, done.changed)
    notify.success(editIndex === null ? 'Row added' : 'Row updated')
    if (myChildren.length > 0 && !closeAfter) {
      setEditIndex(idx)
      setEditorDirty(false)
    } else {
      closeEditor()
    }
  }

  // Validate the row's form when switching away from its own tab to one of its
  // child tabs; block the switch on failure.
  const handleModalTabChange = async (next: string) => {
    if (next === activeTab) return
    if (activeTab === formNo && next !== formNo) {
      if (!(await validateRowForm())) return
      // Leaving the row for one of its child tabs finishes the row too: the
      // child form shows what row_complete built (e.g. the line's taxes).
      if (!(await completeRow())) return
    }
    setActiveTab(next)
  }

  const liveCount = store.rows.filter(r => !r._deleted).length
  // What a line-editor button is told about the line (Vision's updateFlag and
  // detCnt): new or opened, and the lines the form holds — a new line counts
  // itself, as it is already a row of the grid in Vision.
  const lineContext = {
    rowIsNew: editIndex === null,
    lineCount: liveCount + (editIndex === null ? 1 : 0),
  }

  // ── Custom action buttons (on this child form's editor) ────────────────
  // The lines a Vision line action returned (buildVisionActionLines — shared
  // with the spreadsheet's line grid). Which lines the rows become follows
  // MasterStatefulEJB.setActionData: a line Vision has just added blank (not
  // validated yet) takes the first row and the rest are added beside it; on a
  // line that already exists (opened, or selected on the grid) EVERY row is a
  // new line and that line is left as it is. ``from`` is a press on the line
  // grid (its selected line, or none — the blank line Vision adds); absent,
  // the press came from the open editor.
  const visionLineCtx: VisionLineCtx = {
    sm, formNo, parentPk, parentHeaderValues,
    exec: (action, payload) => screen.execute(objName, action, payload),
    onItemChangeError: (m) => notify.error(m),
  }
  const addActionLines = async (
    rows: Array<Record<string, unknown>>,
    from?: { values: Record<string, unknown>; isNew: boolean },
  ) => {
    const pressedOnNew = from ? from.isNew : editIndex === null
    // A new line starts from that blank line; beside an existing one, from a
    // blank line of the form (never the existing line's own values).
    const src = pressedOnNew ? (from ? from.values : editorValues) : null
    const lines = await buildVisionActionLines(visionLineCtx, rows, src, visionLineCol, store.rows)
    for (const line of lines) store.add(withLocalId(line))
    if (lines.length) {
      notify.success(lines.length === 1 ? '1 line added' : `${lines.length} lines added`)
    }
    if (pressedOnNew && !from) closeEditor()
  }

  // Apply an item_change-style action result, or the rows the user picked from
  // a select_grid, to the editor draft (and its grandchildren under __children).
  const applyActionResult = (res: RunActionResponse) => {
    if (res.handler === 'select_grid') {
      const rows = res.rows || []
      if (rows.length === 0) return
      // Rows for THIS form (a Vision line action — despatch's Default returns
      // whole lines, Lot No the lot / location / quantity): each row becomes a
      // line of its own. On a new line they replace the draft; beside an
      // existing line they are added and that line stays as it is
      // (addActionLines).
      if (res.target_form_no && String(res.target_form_no) === formNo) {
        void addActionLines(rows)
        return
      }
      const gc = (res.target_form_no
        && myChildren.find(c => c.formNo === String(res.target_form_no))) || myChildren[0]
      if (gc) {
        setEditorValues(prev => {
          const ch = (prev.__children as EmbedDetailMap | undefined) || {}
          return {
            ...prev,
            __children: { ...ch, [gc.formNo]: [...(ch[gc.formNo] || []), ...rows.map(r => withLocalId(r))] },
          }
        })
        setEditorDirty(true)
      } else {
        setEditorValues(prev => ({ ...prev, ...rows[0] }))
        setEditorDirty(true)
      }
      return
    }
    const set = { ...(res.updates || {}), ...(res.forms?.[formNo]?.set || {}) }
    if (Object.keys(set).length) {
      setEditorValues(prev => ({ ...prev, ...set }))
      setEditorDirty(true)
    }
    if (res.forms) {
      const forms = res.forms
      const childNos = Object.keys(forms).filter(fno => fno !== formNo)
      setEditorValues(prev => {
        const kids = applyFormsToChildren(
          prev.__children as EmbedDetailMap | undefined, forms, childNos, withLocalId)
        return kids.changed ? { ...prev, __children: kids.children } : prev
      })
      if (childNos.some(fno => formChangeTouchesRows(forms[fno]))) setEditorDirty(true)
    }
  }

  // ── Line-grid buttons (Vision OBJ_ACTIONS.PAGE_CONTEXT 1) ─────────────────
  // Vision shows these on the line form's LIST page and runs them on the line
  // selected there (adj_iss Stock sends that line's item / location / lot);
  // with none selected, on the blank line it adds. DISPLAY_MODE still decides
  // whether they show at all (RunActionBar).
  const lineListActions = useMemo(
    () => (sm.actions || []).filter(a => visionOnLineList(a)), [sm])
  const selectedLine = currentLineKey === null ? -1
    : store.rows.findIndex(r => lineKeyOf(r) === currentLineKey)
  const selectedRow = selectedLine >= 0 ? store.rows[selectedLine] : undefined
  const gridLine = useMemo(() => {
    if (selectedRow) return { values: rowToDraft(selectedRow), isNew: !selectedRow._txn_id }
    const blank = literalDefaults(sm)
    if (visionLineCol) blank[columnFormKey(visionLineCol)] = nextVisionLineNo()
    return { values: blank, isNew: true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, sm, visionLineCol, store.rows])
  const applyGridActionResult = (res: RunActionResponse) => {
    if (res.handler === 'select_grid') {
      const rows = res.rows || []
      if (rows.length && (!res.target_form_no || String(res.target_form_no) === formNo)) {
        void addActionLines(rows, gridLine)
      }
      return
    }
    // An item-change style result updates the selected line.
    const set = { ...(res.updates || {}), ...(res.forms?.[formNo]?.set || {}) }
    if (selectedRow && !selectedRow._deleted && Object.keys(set).length) {
      store.edit(selectedLine, { ...set, ...(selectedRow._txn_id ? { _edited: true } : {}) })
    }
  }
  // A locked line has no Save of its own (it opens view-only), so an action's
  // values go onto the line now, like a line-grid button's, and show in the
  // open editor; the record's Save writes them.
  const applyLockedLineResult = (res: RunActionResponse) => {
    const set = { ...(res.updates || {}), ...(res.forms?.[formNo]?.set || {}) }
    const row = editIndex === null ? undefined : store.rows[editIndex]
    if (!row || row._deleted || !Object.keys(set).length) return
    store.edit(editIndex!, { ...set, ...(row._txn_id ? { _edited: true } : {}) })
    setEditorValues(prev => ({ ...prev, ...set }))
  }
  // ── Scan sessions (ActionDef.capture.loop) ─────────────────────────────
  // Started from this form's toolbar on an editable record (a locked form
  // too — business logic filling locked lines is what the flag allows). Each
  // code runs the action; its answer names ONE line of this form, which the
  // user confirms before it changes.
  const sessionActions = useMemo(
    () => (sm.actions || []).map(legacyActionToDef)
      .filter(d => !d.disabled && isScanSessionAction(d) && actionScope(d) === 'form'),
    [sm])
  const [session, setSession] = useState<ActionDef | null>(null)
  const sessionCols = (sm.columns || []).map(w => w.column).filter(Boolean)
  const sessionColKeys = (names: string[]): string[] => {
    const wanted = names.map(n => n.trim().toLowerCase()).filter(Boolean)
    return sessionCols.filter(c => wanted.includes(String(c.name || '').toLowerCase())
      || wanted.includes(String(c.db_name || '').toLowerCase())).map(columnFormKey)
  }
  // The form keys that find the scanned line: capture.match, else the keys.
  const sessionMatchKeys = (def: ActionDef): string[] => {
    const named = actionCaptureOf(def)?.match || []
    return named.length ? sessionColKeys(named) : sessionCols.filter(isKey).map(columnFormKey)
  }
  const sessionText = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
  // capture.pending_while_blank: the lines still to scan (null = not declared).
  const sessionPending = (def: ActionDef, rows = store.rows): number | null => {
    const col = actionCaptureOf(def)?.pending_while_blank
    const key = col ? sessionColKeys([col])[0] : undefined
    if (!key) return null
    return rows.filter(r => !r._deleted && sessionText(r[key]) === '').length
  }
  // A session with nothing left to scan is not offered.
  const shownSessionActions = sessionActions.filter(d => sessionPending(d) !== 0)
  const sessionsOn = (!readOnly || lockedLineActions) && shownSessionActions.length > 0
  const checkSessionCode = async (def: ActionDef, code: string): Promise<ScanSessionCheck> => {
    const capture = actionCaptureOf(def)
    if (!capture) return { kind: 'refused', message: 'This button does not scan.' }
    let res: RunActionResponse
    try {
      res = await runtime.runAction(project, objName, {
        formNo, actionName: def.name,
        header: withCapturedValue({ ...(parentPk || {}) }, capture, code),
        headerValues: parentHeaderValues, parentPk,
      })
    } catch (err) {
      return { kind: 'refused', message: actionErrorMessages(parseApiError(err)).join(' ') || 'Not accepted.' }
    }
    const answer = res.forms?.[formNo]?.rows?.[0]
      || (res.updates && Object.keys(res.updates).length ? res.updates : undefined)
    if (!answer) {
      return { kind: 'refused', message: res.warning || res.prompts?.[0]?.message || 'This code is not on the list.' }
    }
    const keys = sessionMatchKeys(def)
    const index = keys.length && keys.every(k => sessionText(answer[k]) !== '')
      ? store.rows.findIndex(r => !r._deleted && keys.every(k => sessionText(r[k]) === sessionText(answer[k])))
      : -1
    if (index < 0) return { kind: 'refused', message: 'This code is not on the list.' }
    const row = store.rows[index]
    const changes = Object.keys(answer).filter(k => !keys.includes(k)
      && sessionText(answer[k]) !== sessionText(row[k]))
    if (!changes.length) return { kind: 'refused', message: 'Already recorded on this screen.' }
    const details: ScanSessionDetail[] = (sm.columns || []).map(w => w.column)
      .filter(c => c && !isHidden(c) && !/^(add|chg)_(date|user|term)$/i.test(columnFormKey(c)))
      .map(c => {
        const k = columnFormKey(c)
        const now = k in answer ? answer[k] : row[k]
        return {
          label: c.heading || c.name || k,
          value: sessionText(now),
          ...(changes.includes(k) ? { was: sessionText(row[k]) } : {}),
        }
      })
    const firstVisible = details.find(d => d.value)
    return {
      kind: 'match',
      title: firstVisible ? `${firstVisible.label}: ${firstVisible.value}` : 'Scanned line',
      details,
      apply: () => {
        const at = store.rows.findIndex(r => keys.every(k => sessionText(r[k]) === sessionText(answer[k])))
        if (at < 0) return false
        const set = Object.fromEntries(changes.map(k => [k, answer[k]]))
        store.edit(at, { ...set, ...(store.rows[at]._txn_id ? { _edited: true } : {}) })
        // The store re-renders later — count on the line as it now reads.
        const left = sessionPending(def, store.rows.map((r, i) => (i === at ? { ...r, ...set } : r)))
        if (left !== 0) return false
        notify.success(`All lines scanned. Save to record them.`)
        return true
      },
    }
  }
  const sessionButtons = sessionsOn ? shownSessionActions.map(def => (
    <button key={def.name} type="button" className="btn btn-sm"
      onClick={() => setSession(def)} disabled={busy}
      {...tw('action', def.name)}
      style={{ ...btnStyle, color: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}>
      {def.name}
    </button>
  )) : null
  const sessionModal = session ? (
    <ScanSession
      title={session.name}
      prompt={actionCaptureOf(session)?.prompt}
      remaining={sessionPending(session)}
      check={(code) => checkSessionCode(session, code)}
      onClose={() => setSession(null)} />
  ) : null
  // Vision's "Delete All" (SERVICE_HANDLER 'D'): every line goes, the way one
  // line's delete does — a saved line is flagged deleted and is removed on
  // Save, a line not saved yet is dropped (store.remove). Last to first, so
  // dropping an unsaved line never shifts one still to come. No confirmation,
  // as in Vision; a saved line can still be restored before Save.
  const deleteAllLines = () => {
    let n = 0
    for (let i = store.rows.length - 1; i >= 0; i--) {
      if (store.rows[i]._deleted) continue
      store.remove(i)
      n++
    }
    setCurrentLineKey(null)
    if (n) notify.success(n === 1 ? '1 line deleted' : `${n} lines deleted`)
  }
  const gridLineButtons = !readOnly && lineListActions.length > 0 ? (
    <RunActionBar
      actions={lineListActions}
      include={(a) => visionOnLineList(a)}
      onVisionDeleteAll={deleteAllLines}
      inline
      surface={recordTxnId ? 'edit' : 'add'}
      project={project}
      objName={objName}
      formNo={formNo}
      values={gridLine.values}
      headerValues={parentHeaderValues}
      txnId={selectedRow?._txn_id ? String(selectedRow._txn_id) : undefined}
      parentPk={parentPk}
      lineContext={{ rowIsNew: gridLine.isNew, lineCount: liveCount + (selectedRow ? 0 : 1) }}
      onActionResult={applyGridActionResult}
      onActionError={(err) => showActionError(actionErrorMessages(parseApiError(err)))}
    />
  ) : null

  const handleSystemAction = (op: 'add' | 'edit' | 'delete' | 'save' | 'view') => {
    if (op === 'save') void saveRow(false)
    else if (op === 'add') openAdd()
    else if (op === 'delete' && editIndex !== null) { toggleDelete(editIndex); closeEditor() }
    // 'edit' is a no-op while already in the row editor.
  }

  const handleActionError = (err: unknown) => {
    showActionError(actionErrorMessages(parseApiError(err)))
  }

  const formLayout: 'default' | 'compact' = compact ? 'compact' : 'default'

  // The row Add/Edit modal — shared by the grid and compact layouts. In compact
  // mode it opens full-screen and its FormRenderer(s) use the single-column
  // layout. Grandchildren recurse with the same variant.
  // ── Additional attributes in the rows table ─────────────────────────
  // A promoted attribute's cell (n/a / Missing / swatch + label), the line's
  // flags, and the Details drawer — the same UdaRowDrawer the spreadsheet
  // grid opens, editing the line in the buffer with its item change.
  const udaCellOf = (c: ColumnDef, row: Record<string, unknown>, index: number) => (c.uda
    ? <UdaValueCell col={c} value={row[columnFormKey(c)]}
        attrKeys={udaRows.rowOf(udaLineKey(row, index))?.attr_keys}
        fallback={renderCell(row[columnFormKey(c)], c, row, cols)} />
    : renderCell(row[columnFormKey(c)], c, row, cols))
  const udaLineFlags = (udaRows.flags || []).filter(f => (f.show_on || []).includes('line_row'))
  const udaFlagsOf = (row: Record<string, unknown>, index: number) => {
    if (!udaLineFlags.length) return null
    const res = udaRows.rowOf(udaLineKey(row, index))
    const raised = evaluateFlags(udaLineFlags,
      { ...row, ...driverValues(udaRows.columns, res?.drivers) }, { headerValues: parentHeaderValues })
    return raised.length ? <FlagIcons flags={raised} size={22} /> : null
  }
  const udaRowsSm = withUdaColumns(sm, udaRows.columns)
  const indexOfUdaKey = (key: string) => store.rows.findIndex((r, j) => udaLineKey(r, j) === key)
  const udaDrawerChange = (key: string, field: string, value: unknown) => {
    const i = indexOfUdaKey(key)
    if (i < 0 || readOnly) return
    const row = store.rows[i]
    store.edit(i, { [field]: value, ...(row._txn_id ? { _edited: true } : {}) })
    const col = (udaRowsSm.columns || []).map(w => w.column)
      .find(c => !!c && columnFormKey(c) === field)
    if (!col || !isInvokableRule(normalizeItemChange(col.item_change))) return
    const header = rowToDraft({ ...row, [field]: value })
    if (udaDrawerTimer.current != null) window.clearTimeout(udaDrawerTimer.current)
    udaDrawerTimer.current = window.setTimeout(async () => {
      udaDrawerTimer.current = null
      try {
        const res = await screen.execute(objName, 'item_change', {
          form_no: formNo, field_name: field, header, header_values: parentHeaderValues || {},
        }) as { updates?: Record<string, unknown>; protect?: Record<string, unknown>; error?: string }
        if (res?.error) { notify.error(`item_change failed: ${res.error}`); return }
        const j = indexOfUdaKey(key)
        if (j >= 0 && res?.updates && Object.keys(res.updates).length) store.edit(j, res.updates)
        const locks = udaProtectOf(res?.protect)
        if (Object.keys(locks).length) setUdaRowLocks(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...locks } }))
      } catch (err) {
        notify.error(`Updating the line failed: ${parseApiError(err).message}`)
      }
    }, 400)
  }
  const udaDrawerNode = (() => {
    if (!udaDrawerKey) return null
    const i = indexOfUdaKey(udaDrawerKey)
    if (i < 0) return null
    const row = store.rows[i]
    const res = udaRows.rowOf(udaDrawerKey)
    const first = cols.find(c => !c.uda && String(row[columnFormKey(c)] ?? '').trim() !== ''
      && !(c.key === true || String(c.key).toLowerCase() === 'true'))
    const label = first ? String(row[columnFormKey(first)]) : ''
    return (
      <UdaRowDrawer
        title={`Line ${i + 1}${label ? ` · ${label}` : ''}`}
        subject={label || undefined}
        sm={sm}
        columns={udaRows.columns}
        resolution={res}
        values={row}
        errors={(row._fieldErrors as Record<string, string>) || undefined}
        flags={udaLineFlags.length ? evaluateFlags(udaLineFlags,
          { ...row, ...driverValues(udaRows.columns, res?.drivers) }, { headerValues: parentHeaderValues }) : []}
        headerValues={parentHeaderValues}
        onChange={(k, v) => udaDrawerChange(udaDrawerKey, k, v)}
        readOnly={readOnly || !!row._deleted}
        project={project}
        objName={objName}
        formNo={formNo}
        notice={udaRows.error}
        dynamicProtect={udaRowLocks[udaDrawerKey]}
        onClose={() => setUdaDrawerKey(null)}
      />
    )
  })()

  const renderEditor = () => (
    <SubFormModal
      title={editorViewOnly
        ? `View ${sm.form_title || 'row'}`
        : editIndex !== null ? `Edit ${sm.form_title || 'row'}` : `Add ${sm.form_title || 'row'}`}
      banner={editorBanner}
      errors={editorErrors}
      onClose={closeEditor}
      onSave={() => saveRow(false)}
      onSaveAndClose={myChildren.length > 0 ? () => saveRow(true) : undefined}
      busy={busy}
      depth={depth}
      fullScreen={compact}
      viewOnly={editorViewOnly}
      columnLabel={(dbName) => columnLabelFor(sm, dbName)}
    >
      {myChildren.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <FormTabs
            forms={[{ formNo, sm }, ...myChildren]}
            active={activeTab}
            onChange={handleModalTabChange}
            childTabsEnabled={true}
          />
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {activeTab === formNo ? (
              <>
              {udaEditor.error && <UdaNotice message={udaEditor.error} onRetry={udaEditor.retry} />}
              <FormRenderer
                model={lockedEditorSm}
                groupCaptions={udaEditorCaptions}
                groupsHeading={udaEditorHeading}
                values={editorValues}
                errors={editorErrors}
                onChange={onEditorFieldChange}
                readOnlyKey={isExistingRow}
                viewOnly={editorViewOnly}
                actionsInViewOnly={lineActionsOn}
                dynamicProtect={lineEditorLocks}
                project={project}
                objName={objName}
                headerValues={parentHeaderValues}
                formNo={formNo}
                txnId={editorTxnId}
                parentPk={parentPk}
                lineContext={lineContext}
                onActionResult={lineActionsOn ? applyLockedLineResult : applyActionResult}
                onSystemAction={handleSystemAction}
                onActionError={handleActionError}
                layout={formLayout}
              />
              </>
            ) : (() => {
              const child = myChildren.find(c => c.formNo === activeTab)
              if (!child) return null
              if (isVisionTaxForm(child.sm)) {
                // The line's Vision Taxes / Charges panel.
                return (
                  <VisionTaxPanel
                    key={child.formNo}
                    taxSm={child.sm}
                    run={(action, payload) => screen.execute(objName, action,
                      { ...payload, form_no: child.formNo })}
                    line={editorValues}
                    headerValues={parentHeaderValues}
                    heldRows={(editorValues.__children as EmbedDetailMap | undefined)?.[child.formNo]}
                    editMode={!!recordTxnId}
                    recordTxnId={recordTxnId}
                    readOnly={readOnly}
                    onApply={(taxRows, lineChanges) => {
                      setEditorDirty(true)
                      setEditorValues(prev => {
                        const ch = (prev.__children as EmbedDetailMap | undefined) || {}
                        return {
                          ...prev, ...lineChanges,
                          __children: { ...ch, [child.formNo]: taxRows.map(withLocalId) },
                        }
                      })
                    }}
                    onError={(m) => notify.error(m)}
                    onNeedRows={() => {
                      if (isExistingRow) void ensureGrandchildLoaded(child.formNo, childParentPk)
                    }}
                    render={(taxSm, taxStore, taxReadOnly) => (
                      <ChildFormTab
                        project={project}
                        objName={objName}
                        formNo={child.formNo}
                        sm={taxSm}
                        parentSm={sm}
                        parentPk={childParentPk}
                        parentPkKey={childParentPkKey}
                        parentHeaderValues={editorValues}
                        gridLayout={gridLayout}
                        model={model}
                        depth={depth + 1}
                        store={taxStore}
                        readOnly={taxReadOnly}
                        fixedRows
                        variant={variant}
                        visionDialogs={visionDialogs}
                        recordTxnId={editorTxnId}
                        onAncestorPatch={handleDescendantPatch}
                      />
                    )}
                  />
                )
              }
              return (
                <ChildFormTab
                  key={child.formNo}
                  project={project}
                  objName={objName}
                  formNo={child.formNo}
                  sm={child.sm}
                  parentSm={sm}
                  parentPk={childParentPk}
                  parentPkKey={childParentPkKey}
                  parentHeaderValues={editorValues}
                  gridLayout={gridLayout}
                  model={model}
                  depth={depth + 1}
                  store={makeChildStore(child.formNo)}
                  readOnly={readOnly}
                  variant={variant}
                  visionDialogs={visionDialogs}
                  recordTxnId={editorTxnId}
                  onAncestorPatch={handleDescendantPatch}
                />
              )
            })()}
          </div>
        </div>
      ) : (
        <>
        {udaEditor.error && <UdaNotice message={udaEditor.error} onRetry={udaEditor.retry} />}
        <FormRenderer
          model={lockedEditorSm}
          groupCaptions={udaEditorCaptions}
          groupsHeading={udaEditorHeading}
          values={editorValues}
          errors={editorErrors}
          onChange={onEditorFieldChange}
          readOnlyKey={isExistingRow}
          viewOnly={editorViewOnly}
          actionsInViewOnly={lineActionsOn}
          dynamicProtect={lineEditorLocks}
          project={project}
          objName={objName}
          headerValues={parentHeaderValues}
          formNo={formNo}
          txnId={editorTxnId}
          parentPk={parentPk}
          lineContext={lineContext}
          onActionResult={lineActionsOn ? applyLockedLineResult : applyActionResult}
          onSystemAction={handleSystemAction}
          onActionError={handleActionError}
          layout={formLayout}
        />
        </>
      )}
    </SubFormModal>
  )

  // Line Entry (sql_model.line_entry): pull lines from a source document /
  // pick many items. The engine builds every line; they land in this form's
  // buffer like rows added by hand and are verified again at save.
  const lineEntryBar = (
    <LineEntryBar
      sm={sm}
      formNo={formNo}
      execute={(action, payload) => screen.execute(objName, action, payload)}
      engineKey={project}
      headerValues={parentHeaderValues || {}}
      parentPk={parentPk}
      txnId={recordTxnId}
      rows={store.rows}
      onAppend={(rows) => rows.forEach(r => store.add(r))}
      onRemove={(idx) => [...idx].sort((a, b) => b - a).forEach(i => store.remove(i))}
      notify={notify}
      readOnly={readOnly}
      compact={compact}
    />
  )

  // Handheld count (compact view + quick add, canvas board 9).
  const le = useMemo(() => lineEntryOf(sm), [sm])
  const qaBind = useMemo(() => quickAddBinding(le, cols), [le, cols])
  const keyOfRow = (r: Record<string, unknown>) => (r._txn_id != null ? `t:${r._txn_id}` : `l:${r._localId}`)
  const indexOfKey = (k: string) => store.rows.findIndex(r => keyOfRow(r) === k)
  const setCellWithRules = async (rowKey: string, col: string, value: unknown) => {
    const i = indexOfKey(rowKey)
    if (i < 0) return
    const next = { ...store.rows[i], [col]: value }
    store.edit(i, { [col]: value, ...(next._txn_id ? { _edited: true } : {}) })
    // The line's own item_change for the column (an amount from qty × rate).
    try {
      const res = await screen.execute(objName, 'item_change', {
        form_no: formNo, field_name: col, header: next, header_values: parentHeaderValues || {},
      }) as { updates?: Record<string, unknown> }
      if (res?.updates && Object.keys(res.updates).length) {
        const j = indexOfKey(rowKey)
        if (j >= 0) store.edit(j, res.updates)
      }
    } catch (err) {
      notify.error(`Updating the line failed: ${parseApiError(err).message}`)
    }
  }
  const qa = useQuickAdd({
    execute: (action, payload) => screen.execute(objName, action, payload),
    formNo,
    headerValues: parentHeaderValues || {},
    txnId: recordTxnId,
    parentPk,
    rows: store.rows,
    rowKeyOf: keyOfRow,
    onAppend: (added) => added.map(r => {
      const withId = withLocalId(r)
      store.add(withId)
      return keyOfRow(withId)
    }),
    onSetCell: (rk, col, v) => { void setCellWithRules(rk, col, v) },
    onRemoveRow: (rk) => { const i = indexOfKey(rk); if (i >= 0) store.remove(i) },
    labelOf: (k) => {
      const c = cols.find(x => columnFormKey(x) === k)
      return c ? String(c.heading || c.name) : k
    },
  })
  const handheld = compact && !!le?.quickAdd && !readOnly ? (
    <HandheldCount
      qa={qa} rows={store.rows} rowKeyOf={keyOfRow} keys={qaBind.keys} qty={qaBind.qty}
      labelOf={(k) => { const c = cols.find(x => columnFormKey(x) === k); return c ? String(c.heading || c.name) : k }}
      nameOf={(r) => {
        const ki = cols.findIndex(c => qaBind.keys.includes(columnFormKey(c)))
        const nc = cols[ki + 1]
        return nc ? String(r[columnFormKey(nc)] ?? '') : ''
      }}
      onSetCell={(rk, col, v) => { void setCellWithRules(rk, col, v) }}
    />
  ) : null

  if (compact) {
    return (
      <div {...tw('form', formNo, { child: true, rows: liveCount })} style={{
        border: '1px solid var(--color-border)', borderRadius: 12,
        margin: '12px', overflow: 'hidden',
        background: 'var(--lp-input-bg, var(--color-bg))',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '10px 14px',
          background: 'var(--color-surface-2, var(--color-bg-secondary))',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <span style={{
            flex: 1, fontSize: '0.8125rem', fontWeight: 800,
            letterSpacing: 0.3, textTransform: 'uppercase',
            color: 'var(--color-text-secondary)',
          }}>
            {(sm.form_title || 'Rows')} ({loading ? '…' : liveCount})
          </span>
          {lineEntryBar}
          {/* The line form's list-page buttons (Vision PAGE_CONTEXT 1: Stock,
              Delete All…) — the same toolbar the grid layout shows. */}
          {gridLineButtons && !cz && gridLineButtons}
          {sessionButtons}
          {!readOnly && !fixedRows && (
            <button
              type="button" className="btn btn-primary btn-sm"
              onClick={gridAdd}
              disabled={busy}
              title={addOverride?.name}
              {...tw('action', 'add_line')}
              style={{ ...btnStyle, background: 'var(--color-accent)', color: '#fff', border: 'none' }}
            >
              <Plus size={13} /> {addOverride?.name || 'Add'}
            </button>
          )}
        </div>
        {handheld}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!store.rows.some(r => !isReplacedRow(r) && !hiddenRow(r)) && !loading ? (
            <div style={{
              padding: 24, textAlign: 'center',
              color: 'var(--color-text-secondary)', fontSize: '0.9375rem',
            }}>
              {readOnly ? 'No rows.' : `No rows yet — tap "${addOverride?.name || 'Add'}".`}
            </div>
          ) : (
            store.rows.map((row, index) => {
              if (isReplacedRow(row) || hiddenRow(row)) return null
              const deleted = !!row._deleted
              const rowErr = row._error as string | undefined
              return (
                <div
                  key={String(row._localId ?? row._txn_id ?? index)}
                  {...tw('row', index, { key: String(row._txn_id ?? ''), deleted, error: !!rowErr })}
                  role="button"
                  tabIndex={0}
                  onClick={() => (readOnly ? gridView(index) : gridEdit(index))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      if (readOnly) gridView(index)
                      else gridEdit(index)
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '11px 14px',
                    borderBottom: '1px solid var(--color-border)',
                    opacity: deleted ? 0.5 : 1,
                    background: rowErr ? 'rgba(239,68,68,0.07)' : undefined,
                    cursor: 'pointer',
                  }}
                >
                  {rowSelect && <div style={{ paddingTop: 3 }}>{tickBox(row, index)}</div>}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {cols.slice(0, 4).map(c => (
                      <div key={c.col_id || c.name} style={{
                        display: 'flex', gap: 6, fontSize: '0.9625rem',
                        textDecoration: deleted ? 'line-through' : undefined,
                      }}>
                        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600, flexShrink: 0 }}>
                          {listHeadingOf(c)}:
                        </span>
                        <span style={{ color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {udaCellOf(c, row, index)}
                        </span>
                      </div>
                    ))}
                    {rowErr && (
                      <div style={{ color: '#ef4444', fontSize: '0.8125rem', marginTop: 2 }}>{rowErr}</div>
                    )}
                  </div>
                  {!readOnly && !fixedRows && (
                    <button
                      type="button"
                      {...tw('action', deleted ? 'undo_delete_line' : 'delete_line')}
                      onClick={(e) => { e.stopPropagation(); toggleDelete(index) }}
                      style={{ ...iconBtn, flexShrink: 0, color: deleted ? 'var(--color-text-secondary)' : '#ef4444' }}
                      title={deleted ? 'Undo delete' : 'Delete'}
                    >{deleted ? <Undo2 size={15} /> : <Trash2 size={15} />}</button>
                  )}
                </div>
              )
            })
          )}
        </div>

        {cz && (
        <HiddenTray
          title="Not in this grid"
          action="Add"
          hint="Use ⚙ → Show in the grid to take a column out. The line's own form still shows every field."
          items={(sm.columns || []).map(w => w.column).filter(c => c && czIsListHidden(c)).map(c => ({
            key: colKeyOf(c),
            label: String(c.heading || c.name),
            action: 'Add',
            onShow: () => void cz.session.apply(
              czSetLeaf(czColPath(formNo, colKeyOf(c), 'list_hidden'), false), `Add column ${c.heading || c.name}`),
          }))}
        />
      )}
      {editorOpen && renderEditor()}
        {warningsModal}
        {actionErrorModal}
        {validationErrorModal}
        {actionConfirmModal}
        {sessionModal}
      </div>
    )
  }

  return (
    <div {...tw('form', formNo, { child: true, rows: liveCount })}
         style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 16px',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ flex: 1, fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
          {loading ? 'Loading…' : `${liveCount} row${liveCount === 1 ? '' : 's'}`}
        </div>
        <LayoutActions
          show={!!rl.apply && rl.dirty}
          applying={rl.applying}
          onReset={rl.reset}
          onApply={rl.apply ?? (() => {})}
          applyLabel={rl.applyLabel}
          applyTitle={rl.applyTitle}
        />
        {cz && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', color: VIOLET }}>
            Frozen columns
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Freeze one column fewer"
              disabled={frozen <= 0} onClick={() => setFrozen(frozen - 1)} style={btnStyle}>−</button>
            <span style={{ minWidth: 14, textAlign: 'center', fontWeight: 600 }} {...tw('field', 'customize-frozen')}>{frozen}</span>
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Freeze one column more"
              disabled={frozen >= cols.length} onClick={() => setFrozen(frozen + 1)} style={btnStyle}>+</button>
          </span>
        )}
        {/* Make changes: a new column on these lines (Customize never adds). */}
        {cz?.addField && (
          <button type="button" onClick={() => cz.addField?.(formNo, null)} {...tw('action', 'make-changes-add-column')}
            style={{ ...btnStyle, color: VIOLET, borderColor: VIOLET, fontWeight: 600 }}>
            + Add a column
          </button>
        )}
        {lineEntryBar}
        {gridLineButtons && (
          <span
            title={selectedRow ? 'Runs on the selected line' : 'Select a line to run on it'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {gridLineButtons}
          </span>
        )}
        {sessionButtons}
        {!readOnly && !cz && !fixedRows && (
          <button
            type="button" className="btn btn-primary btn-sm"
            onClick={gridAdd}
            disabled={busy}
            title={addOverride?.name}
            {...tw('action', 'add_line')}
            style={{ ...btnStyle, background: 'var(--color-accent)', color: '#fff', border: 'none' }}
          >
            <Plus size={13} /> {addOverride?.name || 'Add row'}
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {!store.rows.some(r => !isReplacedRow(r) && !hiddenRow(r)) && !loading ? (
          <div style={{
            padding: 40, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
          }}>
            {readOnly
              ? 'No rows.'
              : `No rows yet — click "${addOverride?.name || 'Add row'}" to create one.`}
          </div>
        ) : (
          <table style={{
            borderCollapse: 'collapse', fontSize: '0.9375rem',
            // White-in-light / dark-in-dark data surface (theme-adaptive); the
            // transparent rows show this through, so an error row's red tint
            // still shows.
            background: 'var(--lp-input-bg, var(--color-bg))',
            tableLayout: 'fixed',
            width: cols.reduce((sum, c) => sum + wOf(c), actionsW) + (rowSelect ? 44 : 0),
            // Fixed rows (no Add / Delete) sit in a dialog: span it rather
            // than stop short with a gap on the right.
            ...(fixedRows ? { minWidth: '100%' } : {}),
          }}>
            <colgroup>
              {rowSelect && <col style={{ width: 44 }} />}
              {cols.map(c => (
                <col
                  key={c.col_id || c.name}
                  data-col-key={colKeyOf(c)}
                  style={{ width: wOf(c) }}
                />
              ))}
              <col style={{ width: actionsW }} />
            </colgroup>
            <thead style={{
              position: 'sticky', top: 0, background: 'var(--color-surface-2, var(--color-bg-secondary))',
              zIndex: 1,
            }}>
              <tr>
                {rowSelect && (
                  <th style={{ ...th, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all lines"
                      {...tw('action', 'select_all_lines')}
                      checked={allTicked}
                      disabled={selectLocked || busy || shownRows.length === 0}
                      onChange={(e) => tickAll(e.target.checked)}
                      style={{ width: 16, height: 16, margin: 0 }}
                    />
                  </th>
                )}
                {cols.map((c, ci) => {
                  const ck = colKeyOf(c)
                  const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
                  const sticky = stickyLeft(ci)
                  return (
                    <th
                      key={c.col_id || c.name}
                      {...dnd.getHeaderProps(ck)}
                      title="Drag to reorder · drag the right edge to resize"
                      style={{
                        ...th,
                        position: 'relative',
                        cursor: 'grab',
                        paddingRight: cz ? 36 : undefined,
                        ...(sticky ? { ...sticky, zIndex: 2, background: 'var(--color-surface-2, var(--color-bg-secondary))' } : {}),
                        boxShadow: ind === 'left'
                          ? 'inset 3px 0 0 var(--color-accent)'
                          : ind === 'right'
                            ? 'inset -3px 0 0 var(--color-accent)'
                            : sticky?.boxShadow,
                      }}
                    >
                      {listHeadingOf(c)}
                      {cz && cz.session.countCustomized(czColPath(formNo, ck, 'x').slice(0, -2)) > 0 && (
                        <span style={{ marginLeft: 6, display: 'inline-flex' }}><CustomizedDot /></span>
                      )}
                      {cz && (
                        <GearButton label={`Change ${listHeadingOf(c)}`}
                          active={cz.openFieldKey === `${formNo}|${ck}`}
                          onClick={(el) => cz.openField(formNo, ck, el)}
                          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
                      )}
                      <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
                    </th>
                  )
                })}
                <th style={{
                  ...th, textAlign: 'right', paddingLeft: 8, paddingRight: 8,
                  position: 'sticky', right: 0,
                  background: 'var(--color-surface-2, var(--color-bg-secondary))',
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.04)',
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {store.rows.map((row, index) => {
                if (isReplacedRow(row) || hiddenRow(row)) return null
                const deleted = !!row._deleted
                const rowErr = row._error as string | undefined
                return (
                  <tr key={String(row._localId ?? row._txn_id ?? index)}
                    {...tw('row', index, { key: String(row._txn_id ?? ''), deleted, error: !!rowErr })}
                    // Selecting a line is what the line-grid buttons run on.
                    onClick={lineListActions.length
                      ? () => setCurrentLineKey(lineKeyOf(row)) : undefined}
                    aria-selected={selectedLine === index || undefined}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      opacity: deleted ? 0.45 : 1,
                      background: rowErr ? 'rgba(239,68,68,0.07)'
                        : selectedLine === index ? 'rgba(76,141,255,0.12)' : undefined,
                      cursor: lineListActions.length ? 'pointer' : undefined,
                    }}
                  >
                    {rowSelect && (
                      <td style={{ ...td, textAlign: 'center' }}>{tickBox(row, index)}</td>
                    )}
                    {cols.map((c, ci) => (
                      <td key={c.col_id || c.name}
                          data-tw-cell={columnFormKey(c)}
                          style={{
                            ...td,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            textDecoration: deleted ? 'line-through' : undefined,
                            ...stickyLeft(ci),
                          }}
                          title={rowErr || renderTitle(row[columnFormKey(c)], c, row, cols)}
                      >
                        {udaCellOf(c, row, index)}
                      </td>
                    ))}
                    {readOnly ? (
                      <td style={{
                        ...td, textAlign: 'right', whiteSpace: 'nowrap',
                        position: 'sticky', right: 0,
                        background: 'var(--lp-input-bg, var(--color-bg))',
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.04)',
                      }}>
                        {!deleted && udaFlagsOf(row, index) && (
                          <span style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline-flex' }}>{udaFlagsOf(row, index)}</span>
                        )}
                        {udaRows.resolved && !deleted && udaRows.rowOf(udaLineKey(row, index)) && (
                          <span style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline-flex' }}>
                            <UdaDetailsChip
                              counts={detailsCounts(udaRows.columns, row, udaRows.rowOf(udaLineKey(row, index))?.attr_keys)}
                              label={`Open details for line ${index + 1}`}
                              onOpen={() => setUdaDrawerKey(udaLineKey(row, index))}
                            />
                          </span>
                        )}
                        <button type="button" onClick={() => gridView(index)}
                          {...tw('action', 'view_line')}
                          style={iconBtn} title="View"
                        ><Eye size={13} /></button>
                      </td>
                    ) : (
                      <td style={{
                        ...td, textAlign: 'right', whiteSpace: 'nowrap',
                        position: 'sticky', right: 0,
                        background: 'var(--lp-input-bg, var(--color-bg))',
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.04)',
                      }}>
                        {!deleted && udaFlagsOf(row, index) && (
                          <span style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline-flex' }}>{udaFlagsOf(row, index)}</span>
                        )}
                        {udaRows.resolved && !deleted && udaRows.rowOf(udaLineKey(row, index)) && (
                          <span style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline-flex' }}>
                            <UdaDetailsChip
                              counts={detailsCounts(udaRows.columns, row, udaRows.rowOf(udaLineKey(row, index))?.attr_keys)}
                              label={`Open details for line ${index + 1}`}
                              onOpen={() => setUdaDrawerKey(udaLineKey(row, index))}
                            />
                          </span>
                        )}
                        <button type="button" onClick={() => gridEdit(index)}
                          {...tw('action', 'edit_line')}
                          style={iconBtn} title={editOverride?.name || 'Edit'}
                        ><Pencil size={13} /></button>
                        {!fixedRows && <button type="button" onClick={() => toggleDelete(index)}
                          {...tw('action', deleted ? 'undo_delete_line' : 'delete_line')}
                          style={{ ...iconBtn, color: deleted ? 'var(--color-text-secondary)' : '#ef4444' }}
                          title={deleted ? 'Undo delete' : 'Delete'}
                        >{deleted ? <Undo2 size={13} /> : <Trash2 size={13} />}</button>}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {editorOpen && renderEditor()}
      {udaDrawerNode}
      {/* "Ignore warnings and proceed" confirm for a merged system action's
          warning-severity pre-action validations. */}
      {warningsModal}
      {actionErrorModal}
      {validationErrorModal}
      {actionConfirmModal}
      {sessionModal}
    </div>
  )
}

// Whether a form has ANYTHING to validate on navigate-away — a mandatory
// column, an active field-level validation, or an active form-level validation
// (legacy string or non-disabled structured rule). Mirrors what the save
// actually checks, so navigate-away validation matches save; lets us skip the
// round-trip only for forms with truly nothing to validate.
function formNeedsValidation(
  sm: {
    validations?: unknown[]
    columns?: Array<{ column?: { mandatory?: unknown; validations?: unknown[] } }>
  } | null | undefined,
): boolean {
  const active = (list: unknown): boolean =>
    Array.isArray(list) && list.some((v) =>
      typeof v === 'string'
        ? v.trim().length > 0
        : !!v && typeof v === 'object' && !(v as { disabled?: boolean }).disabled,
    )
  if (active(sm?.validations)) return true
  for (const wrap of sm?.columns || []) {
    const col = wrap?.column
    if (!col) continue
    const mand = col.mandatory === true || String(col.mandatory ?? '').toLowerCase() === 'true'
    if (mand || active(col.validations)) return true
  }
  return false
}

function applyValidationErrors(
  items: ValidationErrorItem[], fallbackMessage: string,
  setErrors: (v: Record<string, string>) => void,
  setBanner: (v: string | null) => void,
  notify: TxnNotifier,
) {
  if (items.length === 0) {
    notify.error(fallbackMessage)
    setBanner(fallbackMessage)
    return
  }
  const fieldMap: Record<string, string> = {}
  const general: string[] = []
  for (const it of items) {
    if (it.field && it.field !== '_record') fieldMap[it.field] = it.message
    else general.push(it.message)
  }
  setErrors(fieldMap)
  setBanner(general.length > 0 ? general.join(' · ') : null)
  const first = general[0] || Object.values(fieldMap)[0]
  if (first) notify.error(items.length > 1 ? `${first} (+${items.length - 1} more)` : first)
}

function renderCell(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[]): React.ReactNode {
  if (hasDisplayFormat(col)) {
    return <DisplayCell value={value} col={col} row={row} columns={cols} />
  }
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
  }
  const rich = richCellText(value, col)
  if (rich != null) return rich
  const when = temporalCellText(value, col)
  if (when != null) return when
  // ddlb / radio / checkbox / bool_check: show the display label, not the
  // raw data code (e.g. "Classical" instead of "CLASSICAL").
  const label = ddlbDisplayLabel(value, col)
  if (label != null) return label
  if (col.col_type === 'BOOLEAN') {
    return value === true || value === 'true' || value === 1 ? 'Yes' : 'No'
  }
  return String(value)
}

// Plain-text tooltip for ellipsis-truncated cells.
function renderTitle(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[]): string {
  if (hasDisplayFormat(col)) return displayPlainText(value, col, row, cols)
  if (value === null || value === undefined || value === '') return ''
  const rich = richCellText(value, col)
  if (rich != null) return rich
  const when = temporalCellText(value, col)
  if (when != null) return when
  if (col.col_type === 'BOOLEAN') {
    return value === true || value === 'true' || value === 1 ? 'Yes' : 'No'
  }
  return String(value)
}

function columnLabelFor(sm: SqlModel, key: string): string {
  // ``key`` may be either a form_key (modern validation errors) or a
  // db_name (legacy / PG-error fallback). Match against both.
  for (const w of sm.columns || []) {
    const c = w.column
    if (!c) continue
    if (columnFormKey(c) === key || columnDbName(c) === key) {
      return c.heading || c.name || key
    }
  }
  return key
}

function SubFormModal({
  title, banner, errors, busy, columnLabel, depth = 1, fullScreen = false,
  viewOnly = false, onClose, onSave, onSaveAndClose, children,
}: {
  title: string
  banner: string | null
  errors: Record<string, string>
  busy: boolean
  columnLabel: (dbName: string) => string
  // Nesting depth (1 = a header's direct child). Bumps the z-index so a
  // grandchild editor stacks above its parent editor instead of colliding.
  depth?: number
  // Compact (mobile-style): the modal fills the viewport instead of a centred
  // card, matching the full-screen row editor of the Android app.
  fullScreen?: boolean
  // Read-only row (its header is locked): no Save, Cancel reads "Close".
  viewOnly?: boolean
  onClose: () => void
  onSave: () => void
  // When provided, a second "Save & close" button appears — used for forms
  // that keep the editor open after the first save to manage child rows.
  onSaveAndClose?: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    // ESC closes only the topmost (innermost, highest-depth) editor. Each
    // SubFormModal binds its own listener and stops propagation so an outer
    // editor's listener doesn't also fire.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  // Ctrl+S saves this child row. Scoped to the card and gated on being the
  // topmost layer so a deeper (grandchild) editor or a lookup stacked on top
  // (whose DOM is nested in this card) saves itself instead of this row.
  const cardRef = useRef<HTMLDivElement>(null)
  const { isTopLayer } = useShortcutLayer(true)
  useKeyboardShortcuts(
    [
      { combo: systemCombo('save'), run: () => { if (!busy && !viewOnly && isTopLayer()) onSave() } },
      { combo: systemCombo('save_close'), run: () => {
        if (!busy && !viewOnly && isTopLayer()) (onSaveAndClose ?? onSave)()
      } },
    ],
    { scopeRef: cardRef },
  )
  const errorKeys = Object.keys(errors)
  // Base editor z-index is 9450 (above the record editor 9430 + lookup 9440).
  // Each nesting level adds a band so stacked row editors don't collide.
  const editorZ = Z_CHILD_EDITOR + (depth - 1) * 30
  return (
    <div onClick={onClose}
      style={fullScreen ? {
        position: 'fixed', inset: 0, zIndex: editorZ,
        background: 'var(--color-bg)',
        display: 'flex', flexDirection: 'column',
      } : {
        position: 'fixed', inset: 0, zIndex: editorZ,
        // Lighten the backdrop at depth so a deep stack doesn't go fully black.
        background: depth > 1 ? 'rgba(0,0,0,0.32)' : 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
      <div ref={cardRef} onClick={e => e.stopPropagation()}
        data-tw-dialog="line_editor"
        data-tw-mode={viewOnly ? 'view' : 'edit'}
        style={fullScreen ? {
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          flex: 1, minHeight: 0, width: '100%',
          display: 'flex', flexDirection: 'column',
        } : {
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          width: 'min(820px, 100%)',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 14px 44px rgba(0,0,0,0.45)',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border)',
          fontSize: '0.9625rem', fontWeight: 600,
        }}>
          <span style={{ flex: 1 }}>{title}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close"
                  {...tw('action', 'close')}>
            <X size={16} />
          </button>
        </div>
        {(banner || errorKeys.length > 0) && (
          <div {...tw('msg', 'error', { fields: errorKeys.join(',') })} style={{
            padding: '10px 16px',
            background: 'rgba(239,68,68,0.08)',
            borderBottom: '1px solid rgba(239,68,68,0.35)',
            color: '#ef4444', fontSize: '0.9125rem',
          }}>
            <div style={{ fontWeight: 600 }}>
              {errorKeys.length > 0
                ? `Save blocked — ${errorKeys.length} validation error${errorKeys.length === 1 ? '' : 's'}`
                : 'Save blocked'}
            </div>
            {banner && <div style={{ marginTop: 4 }}>{banner}</div>}
            {errorKeys.length > 0 && (
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {errorKeys.map(k => (
                  <li key={k}><strong>{columnLabel(k)}</strong>: {errors[k]}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {children}
        </div>
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}
                  {...tw('action', viewOnly ? 'close' : 'cancel')}>
            {viewOnly ? 'Close' : 'Cancel'}
          </button>
          {!viewOnly && onSaveAndClose && (
            <button className="btn btn-ghost" onClick={onSaveAndClose} disabled={busy}
              {...tw('action', 'save_close')}
              style={{ padding: '7px 16px', borderRadius: 4 }}
            >
              Save & close
            </button>
          )}
          {!viewOnly && (
          <button className="btn btn-primary" onClick={onSave} disabled={busy}
            {...tw('action', 'save')}
            style={{ background: 'var(--color-accent)', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
          </button>
          )}
        </div>
      </div>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: '0.8425rem',
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg)', color: 'var(--color-text)',
  cursor: 'pointer',
}
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: '4px 6px', color: 'var(--color-text-secondary)',
}
const th: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left',
  // Excel-like gridlines on all sides (the table uses borderCollapse).
  border: '1px solid var(--color-border)',
  fontWeight: 600, fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  letterSpacing: 0.3, textTransform: 'uppercase',
}
const td: React.CSSProperties = {
  padding: '8px 12px',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
}
